<template>
  <div class="punch-page">
    <h2 class="page-title">小课打卡</h2>
    <el-card>
      <p class="intro">下面列出全部小课学员。默认直接划小课，不够记欠；要用大课抵请在下方改选。单次最多 80 人。</p>
      <el-input
        v-model="keyword"
        clearable
        placeholder="学员姓名 / 拼音 / 电话"
        class="search-input"
      />
      <div class="select-bar">
        <span>已选 {{ selectedIds.length }} 人</span>
        <div class="select-actions">
          <el-button v-if="keyword.trim() && keywordMatches.length" type="text" @click="toggleSelectFiltered">
            {{ allKeywordSelected ? '取消搜索结果' : '全选搜索结果' }}
          </el-button>
          <el-button v-if="selectedIds.length" type="text" @click="selectedIds = []">清空已选</el-button>
        </div>
      </div>
      <div v-if="selectedStudents.length" class="picked">
        <el-tag
          v-for="s in selectedStudents"
          :key="s.id"
          closable
          type="success"
          class="picked-tag"
          @close="unselect(s.id)"
        >
          {{ s.name }}（家长：{{ s.parentName || '未填' }}）
        </el-tag>
      </div>
      <div class="result-list">
        <div class="section-title">{{ listTitle }}</div>
        <div v-if="!searchResults.length && !selectedStudents.length" class="empty">{{ keyword.trim() ? '没有匹配学员，换个关键词试试' : '暂无小课学员' }}</div>
        <div
          v-for="s in searchResults"
          :key="s.id"
          class="student-row"
          @click="toggle(s.id)"
        >
          <span class="check"></span>
          <div class="meta">
            <div class="name">{{ s.name }}（家长：{{ s.parentName || '未填' }}）</div>
            <div class="sub">小课剩 {{ s.privateRemainingHours || 0 }}{{ s.privateExchangeEnabled === 1 ? ' · 大课剩 ' + (s.remainingHours || 0) + ' · 可抵 ' + s.privateExchangeRate + '大课=1小课' : '' }} · 教练 {{ s.privateCoachName || '未指定' }}</div>
          </div>
        </div>
        <el-button v-if="hasMorePreview" type="text" @click="showMorePreview" style="width:100%">
          再显示一些（还有 {{ moreCount }} 人）
        </el-button>
      </div>

      <el-form label-width="90px" class="punch-form">
        <el-form-item label="本次怎么扣">
          <el-radio-group v-model="payMode">
            <el-radio label="private">直接划小课</el-radio>
            <el-radio label="exchange">大课抵小课</el-radio>
          </el-radio-group>
          <div class="pay-hint">{{ payMode === 'exchange' ? '小课不够时按该学员已设比例扣大课；未开通或大课不够会失败，不会自动记欠。' : '小课够就划小课，不够记欠。不会自动用大课抵。' }}</div>
        </el-form-item>
        <el-form-item label="每人课时">
          <el-input-number v-model="hours" :min="0.5" :max="10" :step="0.5" :precision="1" />
          <span style="margin-left:8px;color:#64748b;font-size:12px">须为 0.5 的倍数，常用 0.5 或 1 节</span>
        </el-form-item>
        <el-form-item label="上课日期">
          <el-date-picker v-model="lessonDate" type="date" value-format="yyyy-MM-dd" style="width:100%" />
        </el-form-item>
        <el-form-item label="上课时间">
          <el-time-picker v-model="lessonTime" value-format="HH:mm" format="HH:mm" style="width:100%" />
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="remark" type="textarea" :rows="2" />
        </el-form-item>
        <el-form-item>
          <el-button type="success" :loading="loading" :disabled="loading" @click="handleSubmit" style="width:100%">
            确认打卡（{{ selectedIds.length }} 人）
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>
  </div>
</template>

<script>
import { getPrivateStudents, batchPrivatePunch } from '../../api'
import pinyinSearch from '../../utils/pinyin-search'

function formatNow() {
  const d = new Date()
  const p = n => (n < 10 ? '0' + n : '' + n)
  return {
    date: d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate()),
    time: p(d.getHours()) + ':' + p(d.getMinutes())
  }
}

export default {
  name: 'PrivatePunch',
  data() {
    const now = formatNow()
    return {
      students: [],
      selectedIds: [],
      keyword: '',
      previewLimit: pinyinSearch.PREVIEW_SIZE,
      hours: 1,
      payMode: 'private',
      lessonDate: now.date,
      lessonTime: now.time,
      remark: '',
      loading: false
    }
  },
  computed: {
    selectedStudents() {
      const set = {}
      this.selectedIds.forEach(id => { set[id] = true })
      return this.students.filter(s => set[s.id])
    },
    keywordMatches() {
      const kw = (this.keyword || '').trim()
      if (!kw) return []
      return this.students.filter(s => pinyinSearch.matchStudent(s, kw, [s.privateCoachName]))
    },
    searchResults() {
      const set = {}
      this.selectedIds.forEach(id => { set[id] = true })
      const kw = (this.keyword || '').trim()
      const pool = kw ? this.keywordMatches : this.students
      const unselected = pool.filter(s => !set[s.id])
      return kw ? unselected : unselected.slice(0, this.previewLimit)
    },
    moreCount() {
      if ((this.keyword || '').trim()) return 0
      const set = {}
      this.selectedIds.forEach(id => { set[id] = true })
      return Math.max(0, this.students.filter(s => !set[s.id]).length - this.searchResults.length)
    },
    hasMorePreview() {
      return this.moreCount > 0
    },
    listTitle() {
      const kw = (this.keyword || '').trim()
      if (kw) return '搜索结果 ' + this.searchResults.length
      const shown = this.selectedStudents.length + this.searchResults.length
      return '小课学员（先显示 ' + shown + ' / 共 ' + this.students.length + ' 人）'
    },
    allKeywordSelected() {
      return this.keywordMatches.length > 0 && this.keywordMatches.every(s => this.selectedIds.indexOf(s.id) >= 0)
    }
  },
  mounted() {
    this.loadStudents()
  },
  methods: {
    async loadStudents() {
      const all = []
      let page = 1
      const size = 200
      while (page <= 30) {
        const res = await getPrivateStudents({ page, size })
        const records = (res.data && res.data.records) || []
        const total = Number((res.data && res.data.total) || 0)
        all.push.apply(all, records)
        if (!records.length || all.length >= total || records.length < size) break
        page++
      }
      this.students = all
      if (this.$route.query.id) {
        const id = Number(this.$route.query.id)
        if (id && this.selectedIds.indexOf(id) < 0) this.selectedIds = [id]
      }
    },
    toggle(id) {
      const i = this.selectedIds.indexOf(id)
      if (i >= 0) this.selectedIds.splice(i, 1)
      else this.selectedIds.push(id)
    },
    unselect(id) {
      this.selectedIds = this.selectedIds.filter(x => x !== id)
    },
    showMorePreview() {
      this.previewLimit += pinyinSearch.PREVIEW_SIZE
    },
    toggleSelectFiltered() {
      const ids = this.keywordMatches.map(s => s.id)
      if (!ids.length) return
      if (this.allKeywordSelected) {
        const drop = {}
        ids.forEach(id => { drop[id] = true })
        this.selectedIds = this.selectedIds.filter(id => !drop[id])
      } else {
        const have = {}
        this.selectedIds.forEach(id => { have[id] = true })
        ids.forEach(id => { if (!have[id]) this.selectedIds.push(id) })
      }
    },
    async handleSubmit() {
      if (this.loading) return
      if (!this.selectedIds.length) {
        this.$message.warning('请勾选到课学员')
        return
      }
      if (this.selectedIds.length > 80) {
        this.$message.warning('单次最多 80 人，请分批消课')
        return
      }
      if (this.payMode === 'exchange') {
        const missing = this.selectedStudents.filter(s => !(s.privateExchangeEnabled === 1 && Number(s.privateExchangeRate) >= 2))
        if (missing.length) {
          const names = missing.slice(0, 3).map(s => s.name).join('、')
          const more = missing.length > 3 ? ' 等' + missing.length + '人' : ''
          this.$message.warning(names + more + '未开通大课抵小课，请改选划小课或先在名单里开通')
          return
        }
      }
      this.loading = true
      try {
        const res = await batchPrivatePunch({
          studentIds: this.selectedIds,
          hours: Number(this.hours).toFixed(1),
          lessonTime: this.lessonDate + ' ' + this.lessonTime,
          remark: this.remark,
          useGroupExchange: this.payMode === 'exchange'
        })
        const data = res.data || {}
        this.$alert(data.message || ('已处理 ' + this.selectedIds.length + ' 人'), '消课完成')
          .then(() => this.$router.push('/private/records'))
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.page-title { margin-bottom: 20px; }
.intro { font-size: 13px; color: #64748B; margin: 0 0 16px; line-height: 1.6; }
.search-input { margin-bottom: 12px; }
.select-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  font-size: 13px;
  color: #64748B;
}
.picked { margin-bottom: 12px; }
.picked-tag { margin: 0 8px 8px 0; }
.section-title { font-size: 12px; color: #94A3B8; margin-bottom: 8px; }
.result-list { max-height: 360px; overflow: auto; margin-bottom: 16px; border: 1px solid #F1F5F9; border-radius: 8px; padding: 8px 12px; }
.student-row {
  display: flex;
  align-items: center;
  padding: 8px 4px;
  cursor: pointer;
  border-bottom: 1px solid #F8FAFC;
}
.student-row:hover { background: #F0FDFA; }
.check {
  width: 16px;
  height: 16px;
  border: 2px solid #94A3B8;
  border-radius: 4px;
  margin-right: 10px;
  flex-shrink: 0;
}
.meta { flex: 1; }
.name { font-size: 14px; color: #0F172A; font-weight: 600; }
.sub { font-size: 12px; color: #64748B; margin-top: 2px; }
.empty { text-align: center; color: #94A3B8; padding: 16px 0; font-size: 13px; }
.punch-form { max-width: 560px; }
.pay-hint { margin-top: 6px; font-size: 12px; color: #94A3B8; line-height: 1.5; }
@media (max-width: 768px) {
  .punch-form { max-width: 100%; }
  .punch-form >>> .el-form-item__label { width: 80px !important; }
}
</style>
